import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  Typography,
  TableSortLabel
} from '@mui/material';

const RawMaterialStockReportTable = ({ reportData, loading, error }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedData = React.useMemo(() => {
    if (!sortColumn) return reportData;
    return [...reportData].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];
      // Handle date
      if (sortColumn === 'date') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (!aValue) return 1;
      if (!bValue) return -1;
      return sortDirection === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [reportData, sortColumn, sortDirection]);

  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) return <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />;
  if (error) return <Typography color="error">{error}</Typography>;

  const columns = [
    { id: 'date', label: 'Date' },
    { id: 'stock_code', label: 'Stock Code' },
    { id: 'stock_name', label: 'Stock Name' },
    { id: 'stock_type', label: 'Stock Type' },
    { id: 'karatage', label: 'Karatage' },
    { id: 'color', label: 'Color' },
    { id: 'stone_type', label: 'Stone Type' },
    { id: 'qty', label: 'Qty' },
    { id: 'weight', label: 'Weight' },
    { id: 'pure_weight', label: 'Pure Weight' },
    { id: 'remarks', label: 'Remarks' },
    { id: 'created_by', label: 'Created By' },
    { id: 'created_at', label: 'Created At' }
  ];

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell key={col.id} sortDirection={sortColumn === col.id ? sortDirection : false}>
                  <TableSortLabel
                    active={sortColumn === col.id}
                    direction={sortColumn === col.id ? sortDirection : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell>{row.date ? new Date(row.date).toLocaleDateString() : ''}</TableCell>
                <TableCell>{row.stock_code}</TableCell>
                <TableCell>{row.stock_name}</TableCell>
                <TableCell>{row.stock_type}</TableCell>
                <TableCell>{row.karatage}</TableCell>
                <TableCell>{row.color}</TableCell>
                <TableCell>{row.stone_type}</TableCell>
                <TableCell>{Number(row.qty).toFixed(2)}</TableCell>
                <TableCell>{Number(row.weight).toFixed(3)}</TableCell>
                <TableCell>{Number(row.pure_weight).toFixed(2)}</TableCell>
                <TableCell>{row.remarks}</TableCell>
                <TableCell>{row.created_by}</TableCell>
                <TableCell>{row.created_at ? new Date(row.created_at).toLocaleDateString() : ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={reportData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default RawMaterialStockReportTable; 